import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, forkJoin, map, Observable, of, shareReplay, Subscription, switchMap } from 'rxjs';

import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { LoadingBlockComponent } from '../../../shared/ui/loading-block/loading-block.component';
import { EmptyStateComponent } from '../../../shared/ui/empty-state/empty-state.component';
import { ProcedureTypeItem } from '../../procedure-types/data-access/procedure-types.models';
import { SlotItem, SlotStatus } from '../../slots/data-access/slots.models';
import { SlotsService } from '../../slots/data-access/slots.service';
import { AppointmentsService } from '../data-access/appointments.service';
import {
  AppointmentChangeRequestItem,
  AppointmentListItem,
  ClientAppointmentCreateContextDto,
  CreateAppointmentChangeRequest,
  CreateAppointmentRequest,
  UpdateAppointmentRequest
} from '../data-access/appointments.models';
import {
  AppointmentClientSpecialistIntent,
  AppointmentFormComponent,
  AppointmentFormPrefill,
  AppointmentSlotScopeChange
} from '../components/appointment-form.component';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { AuthService } from '../../../core/auth/auth.service';
import { UserProfilePageComponent } from '../../users/pages/user-profile-page.component';
import { DeviceQuickViewModalComponent } from '../../devices/components/device-quick-view-modal.component';
import { ClinicQuickViewModalComponent } from '../../clinics/components/clinic-quick-view-modal.component';
import { ProcedureTypeQuickViewModalComponent } from '../../procedure-types/components/procedure-type-quick-view-modal.component';
import { SpecialistQuickViewModalComponent } from '../../specialists/components/specialist-quick-view-modal.component';

@Component({
  selector: 'app-appointment-edit-page',
  standalone: true,
  imports: [
    PageHeaderComponent,
    LoadingBlockComponent,
    EmptyStateComponent,
    AppointmentFormComponent,
    DeviceQuickViewModalComponent,
    ClinicQuickViewModalComponent,
    ProcedureTypeQuickViewModalComponent,
    SpecialistQuickViewModalComponent,
    UserProfilePageComponent,
    TranslatePipe
  ],
  template: `
    @if (!modalPresentation) {
      <app-page-header
        title="Edit appointment"
        subtitle="Update pending request before confirmation"
      />
    }

    @if (isLoading) {
      <app-loading-block text="Loading appointments..." />
    } @else if (loadErrorText) {
      <app-empty-state
        title="Failed to load appointments"
        [description]="loadErrorText"
      />
    } @else {
      <div class="appointment-edit-page" [class.appointment-edit-page--modal]="modalPresentation">
        @if (!isClientActor) {
          <label class="appointment-edit-page__lock-toggle">
            <input
              type="checkbox"
              [checked]="clientChangesLocked"
              (change)="onClientLockChanged($event)"
            />
            {{ 'Lock client time changes (client can only accept or cancel)' | t }}
          </label>
        }

        @if (isClientActor) {
          <div class="appointment-edit-page__summary">
            <div class="appointment-edit-page__summary-title">{{ 'Requested appointment change' | t }}</div>
            <div class="appointment-edit-page__summary-grid">
              <div>
                <strong>{{ 'Clinic' | t }}:</strong>
                @if (canOpenClinic && currentClinicId?.trim()) {
                  <button
                    type="button"
                    class="appointment-edit-page__link-button"
                    (click)="openClinic(currentClinicId ?? '')"
                    [title]="currentClinicLabel"
                  >
                    {{ currentClinicLabel }}
                  </button>
                } @else {
                  {{ currentClinicLabel }}
                }
              </div>
              <div>
                <strong>{{ 'Procedure' | t }}:</strong>
                @if (canOpenProcedure && currentProcedureId?.trim()) {
                  <button
                    type="button"
                    class="appointment-edit-page__link-button"
                    (click)="openProcedure(currentProcedureId ?? '')"
                    [title]="currentProcedureLabel"
                  >
                    {{ currentProcedureLabel }}
                  </button>
                } @else {
                  {{ currentProcedureLabel }}
                }
              </div>
              <div>
                <strong>{{ 'Device' | t }}:</strong>
                @if (canOpenDevice && currentDeviceId?.trim()) {
                  <button
                    type="button"
                    class="appointment-edit-page__link-button"
                    (click)="openDevice(currentDeviceId ?? '')"
                    [title]="currentDeviceLabel"
                  >
                    {{ currentDeviceLabel }}
                  </button>
                } @else {
                  {{ currentDeviceLabel }}
                }
              </div>
              <div>
                <strong>{{ 'Specialist' | t }}:</strong>
                @if (canOpenSpecialist && currentSpecialistId?.trim()) {
                  <button
                    type="button"
                    class="appointment-edit-page__link-button"
                    (click)="openSpecialist(currentSpecialistId ?? '')"
                    [title]="currentSpecialistLabel"
                  >
                    {{ currentSpecialistLabel }}
                  </button>
                } @else {
                  {{ currentSpecialistLabel }}
                }
              </div>
              <div><strong>{{ 'Current time' | t }}:</strong> {{ currentSlotSummary }}</div>
            </div>
          </div>

          <div class="appointment-edit-page__client-notice">
            {{
              'Your requested change will be sent for review. Please wait for staff confirmation before considering the appointment rescheduled.' | t
            }}
          </div>

          <div class="appointment-edit-page__request-note">
            <label for="request-note">{{ 'Request Note' | t }}</label>
            <textarea
              id="request-note"
              [value]="requestNote"
              (input)="onRequestNoteInput($event)"
              placeholder="Optional note"
            ></textarea>
          </div>
        }

        <app-appointment-form
          mode="edit"
          [isSubmitting]="isSubmitting"
          [errorText]="errorText"
          [prefill]="prefill"
          [procedureTypes]="procedureTypes"
          [clinicProcedureAssignments]="clinicProcedureAssignments"
          [availableSlots]="availableSlots"
          [clinics]="clinicOptions"
          [devices]="deviceOptions"
          [specialists]="specialistOptions"
          [clients]="clientOptions"
          [lockedClientId]="lockedClientId"
          [clientIntentMode]="isClientActor"
          [availabilityCalendarMode]="true"
          [minCalendarDate]="clientMinCalendarDate"
          [initialClientSpecialistIntent]="initialClientSpecialistIntent"
          [hideTechnicalIds]="isClientActor"
          [showConfirmationRequired]="!isClientActor"
          [submitLabel]="isClientActor ? 'Request appointment change' : null"
          [submittingLabel]="isClientActor ? 'Sending request...' : null"
          (submitted)="onSubmitted($event)"
          (slotScopeChanged)="onSlotScopeChanged($event)"
        />

        @if (changeRequests.length > 0) {
          <div class="appointment-edit-page__history">
            <div class="appointment-edit-page__history-title">
              {{ 'Change request history' | t }}
            </div>

            <div class="appointment-edit-page__history-table-wrap">
              <table class="appointment-edit-page__history-table">
                <thead>
                  <tr>
                    @if (!isClientActor) {
                      <th>{{ 'Client' | t }}</th>
                    }
                    <th class="appointment-edit-page__history-col appointment-edit-page__history-col--created">{{ 'Created At' | t }}</th>
                    <th>{{ 'common.status' | t }}</th>
                    <th>{{ 'Requested vs Current' | t }}</th>
                    <th class="appointment-edit-page__history-col appointment-edit-page__history-col--request-note">{{ 'Request Note' | t }}</th>
                    <th class="appointment-edit-page__history-col appointment-edit-page__history-col--review-note">{{ 'Review Note' | t }}</th>
                    <th class="appointment-edit-page__history-col appointment-edit-page__history-col--reviewed">{{ 'Reviewed At' | t }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (item of changeRequests; track item.id) {
                    <tr>
                      @if (!isClientActor) {
                        <td>
                          @if (canOpenClient && lockedClientId?.trim()) {
                            <button
                              type="button"
                              class="appointment-edit-page__link-button"
                              (click)="openClient(lockedClientId ?? '')"
                              [title]="clientTitle(lockedClientId)"
                            >
                              {{ clientLabel(lockedClientId) }}
                            </button>
                          } @else {
                            <div [title]="clientTitle(lockedClientId)">{{ clientLabel(lockedClientId) }}</div>
                          }
                          @if (showIdMeta(lockedClientId)) {
                            <div class="appointment-edit-page__id-meta">{{ lockedClientId }}</div>
                          }
                        </td>
                      }
                      <td class="appointment-edit-page__history-col appointment-edit-page__history-col--created">{{ item.createdAt }}</td>
                      <td>{{ item.status }}</td>
                      <td>
                        <div class="appointment-edit-page__diff">
                          <div>
                            <strong>{{ 'Clinic' | t }}:</strong>
                            @if (canOpenClinic && item.currentSnapshot?.clinicId?.trim()) {
                              <button
                                type="button"
                                class="appointment-edit-page__link-button"
                                (click)="openClinic(item.currentSnapshot?.clinicId ?? '')"
                              >
                                {{ snapshotValue(item.currentSnapshot?.clinicId, 'clinic') }}
                              </button>
                            } @else {
                              {{ snapshotValue(item.currentSnapshot?.clinicId, 'clinic') }}
                            }
                            ->
                            @if (canOpenClinic && item.requestedSnapshot?.clinicId?.trim()) {
                              <button
                                type="button"
                                class="appointment-edit-page__link-button"
                                (click)="openClinic(item.requestedSnapshot?.clinicId ?? '')"
                              >
                                {{ snapshotValue(item.requestedSnapshot?.clinicId, 'clinic') }}
                              </button>
                            } @else {
                              {{ snapshotValue(item.requestedSnapshot?.clinicId, 'clinic') }}
                            }
                          </div>
                          <div>
                            <strong>{{ 'Procedure' | t }}:</strong>
                            @if (canOpenProcedure && item.currentSnapshot?.procedureTypeId?.trim()) {
                              <button
                                type="button"
                                class="appointment-edit-page__link-button"
                                (click)="openProcedure(item.currentSnapshot?.procedureTypeId ?? '')"
                              >
                                {{ snapshotValue(item.currentSnapshot?.procedureTypeId, 'procedure') }}
                              </button>
                            } @else {
                              {{ snapshotValue(item.currentSnapshot?.procedureTypeId, 'procedure') }}
                            }
                            ->
                            @if (canOpenProcedure && item.requestedSnapshot?.procedureTypeId?.trim()) {
                              <button
                                type="button"
                                class="appointment-edit-page__link-button"
                                (click)="openProcedure(item.requestedSnapshot?.procedureTypeId ?? '')"
                              >
                                {{ snapshotValue(item.requestedSnapshot?.procedureTypeId, 'procedure') }}
                              </button>
                            } @else {
                              {{ snapshotValue(item.requestedSnapshot?.procedureTypeId, 'procedure') }}
                            }
                          </div>
                          <div>
                            <strong>{{ 'Device' | t }}:</strong>
                            @if (canOpenDevice && item.currentSnapshot?.deviceId?.trim()) {
                              <button
                                type="button"
                                class="appointment-edit-page__link-button"
                                (click)="openDevice(item.currentSnapshot?.deviceId ?? '')"
                              >
                                {{ snapshotValue(item.currentSnapshot?.deviceId, 'device') }}
                              </button>
                            } @else {
                              {{ snapshotValue(item.currentSnapshot?.deviceId, 'device') }}
                            }
                            ->
                            @if (canOpenDevice && item.requestedSnapshot?.deviceId?.trim()) {
                              <button
                                type="button"
                                class="appointment-edit-page__link-button"
                                (click)="openDevice(item.requestedSnapshot?.deviceId ?? '')"
                              >
                                {{ snapshotValue(item.requestedSnapshot?.deviceId, 'device') }}
                              </button>
                            } @else {
                              {{ snapshotValue(item.requestedSnapshot?.deviceId, 'device') }}
                            }
                          </div>
                          <div>
                            <strong>{{ 'Specialist' | t }}:</strong>
                            @if (canOpenSpecialist && item.currentSnapshot?.specialistUserId?.trim()) {
                              <button
                                type="button"
                                class="appointment-edit-page__link-button"
                                (click)="openSpecialist(item.currentSnapshot?.specialistUserId ?? '')"
                              >
                                {{ snapshotValue(item.currentSnapshot?.specialistUserId, 'specialist') }}
                              </button>
                            } @else {
                              {{ snapshotValue(item.currentSnapshot?.specialistUserId, 'specialist') }}
                            }
                            ->
                            @if (canOpenSpecialist && item.requestedSnapshot?.specialistUserId?.trim()) {
                              <button
                                type="button"
                                class="appointment-edit-page__link-button"
                                (click)="openSpecialist(item.requestedSnapshot?.specialistUserId ?? '')"
                              >
                                {{ snapshotValue(item.requestedSnapshot?.specialistUserId, 'specialist') }}
                              </button>
                            } @else {
                              {{ snapshotValue(item.requestedSnapshot?.specialistUserId, 'specialist') }}
                            }
                          </div>
                          <div>
                            <strong>{{ 'Slots' | t }}:</strong>
                            {{ slotSnapshotSummary(item.currentSnapshot?.slotIds) }}
                            -> {{ slotSnapshotSummary(item.requestedSnapshot?.slotIds) }}
                          </div>
                        </div>
                      </td>
                      <td class="appointment-edit-page__history-col appointment-edit-page__history-col--request-note">{{ item.note ?? '—' }}</td>
                      <td class="appointment-edit-page__history-col appointment-edit-page__history-col--review-note">{{ item.reviewNote ?? '—' }}</td>
                      <td class="appointment-edit-page__history-col appointment-edit-page__history-col--reviewed">{{ item.reviewedAt ?? '—' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }

        @if (isClientProfileModalOpen && selectedClientProfileId) {
          <div class="appointment-edit-page__modal-backdrop" (click)="closeClientProfileModal()">
            <div
              class="appointment-edit-page__modal"
              (click)="$event.stopPropagation()"
            >
              <div class="appointment-edit-page__modal-head">
                <div>
                  <h3 class="appointment-edit-page__modal-title">{{ 'Client Profile' | t }}</h3>
                  <p class="appointment-edit-page__modal-subtitle">
                    {{ 'CRM client context without leaving the current appointment flow.' | t }}
                  </p>
                </div>

                <button
                  type="button"
                  class="appointment-edit-page__modal-close"
                  (click)="closeClientProfileModal()"
                  aria-label="Close client profile modal"
                >
                  ×
                </button>
              </div>

              <app-user-profile-page
                [clientIdOverride]="selectedClientProfileId"
                presentationMode="quick-view"
              />
            </div>
          </div>
        }

        @if (isDeviceQuickViewOpen && selectedDeviceId) {
          <app-device-quick-view-modal
            [isOpen]="true"
            [deviceId]="selectedDeviceId"
            title="Device Quick View"
          (close)="closeDeviceQuickView()"
        />
      }

        @if (isClinicQuickViewOpen && selectedClinicId) {
          <app-clinic-quick-view-modal
            [isOpen]="true"
            [clinicId]="selectedClinicId"
            title="Clinic Quick View"
            (close)="closeClinicQuickView()"
          />
        }

        @if (isProcedureQuickViewOpen && selectedProcedureTypeId) {
          <app-procedure-type-quick-view-modal
            [isOpen]="true"
            [procedureTypeId]="selectedProcedureTypeId"
            title="Procedure Type Quick View"
            (close)="closeProcedureQuickView()"
          />
        }

        @if (isSpecialistQuickViewOpen && selectedSpecialistUserId) {
          <app-specialist-quick-view-modal
            [isOpen]="true"
            [specialistUserId]="selectedSpecialistUserId"
            title="Specialist Quick View"
            (close)="closeSpecialistQuickView()"
          />
        }
      </div>
    }
  `,
  styles: [`
    .appointment-edit-page {
      display: grid;
      gap: 14px;
    }

    .appointment-edit-page--modal {
      padding-top: 4px;
    }

    .appointment-edit-page__modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      background: rgba(15, 23, 42, 0.52);
      backdrop-filter: blur(6px);
    }

    .appointment-edit-page__modal {
      width: min(980px, 100%);
      max-height: min(90vh, 960px);
      overflow: auto;
      border-radius: 24px;
      border: 1px solid rgba(226, 232, 240, 0.9);
      background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.97));
      box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
      padding: 22px;
      box-sizing: border-box;
    }

    .appointment-edit-page__modal-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 16px;
    }

    .appointment-edit-page__modal-title {
      margin: 0;
      font-size: 24px;
      line-height: 1.1;
      color: #111827;
    }

    .appointment-edit-page__modal-subtitle {
      margin: 8px 0 0;
      color: #6b7280;
      font-size: 14px;
      line-height: 1.5;
    }

    .appointment-edit-page__modal-close {
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      border: 1px solid #dbe2ea;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.92);
      color: #475569;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
    }

    .appointment-edit-page__lock-toggle {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
      font-size: 14px;
      color: #111827;
    }

    .appointment-edit-page__summary {
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96));
      padding: 14px;
      display: grid;
      gap: 10px;
    }

    .appointment-edit-page__summary-title {
      font-size: 14px;
      font-weight: 700;
      color: #111827;
    }

    .appointment-edit-page__summary-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px 14px;
      font-size: 13px;
      color: #374151;
    }

    .appointment-edit-page__client-notice {
      border: 1px solid #bfdbfe;
      border-radius: 8px;
      background: #eff6ff;
      color: #1e3a8a;
      padding: 10px 12px;
      font-size: 13px;
      line-height: 1.45;
    }

    .appointment-edit-page__request-note {
      margin-bottom: 12px;
      display: grid;
      gap: 8px;
    }

    .appointment-edit-page__request-note label {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    .appointment-edit-page__request-note textarea {
      min-height: 80px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 8px 10px;
      box-sizing: border-box;
      resize: vertical;
      font-family: inherit;
      font-size: 13px;
    }

    .appointment-edit-page__history {
      margin-top: 16px;
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 12px;
    }

    .appointment-edit-page__history-title {
      font-size: 14px;
      font-weight: 600;
      color: #111827;
      margin-bottom: 10px;
    }

    .appointment-edit-page__history-table-wrap {
      overflow-x: auto;
    }

    .appointment-edit-page__history-table {
      width: 100%;
      min-width: 1180px;
      border-collapse: collapse;
    }

    .appointment-edit-page__history-table th,
    .appointment-edit-page__history-table td {
      text-align: left;
      padding: 10px 12px;
      border-bottom: 1px solid #f3f4f6;
      font-size: 12px;
      vertical-align: top;
    }

    .appointment-edit-page__history-table th {
      background: #f9fafb;
      color: #374151;
    }

    .appointment-edit-page__id-meta {
      margin-top: 2px;
      color: #6b7280;
      font-size: 11px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      word-break: break-all;
      line-height: 1.25;
    }

    .appointment-edit-page__diff {
      display: grid;
      gap: 3px;
      min-width: 260px;
      font-size: 11px;
      color: #374151;
    }

    .appointment-edit-page__link-button {
      all: unset;
      cursor: pointer;
      color: #2563eb;
      text-decoration: underline;
      text-underline-offset: 2px;
      display: inline-block;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    @media (max-width: 900px) {
      .appointment-edit-page__history-table {
        min-width: 700px;
      }

      .appointment-edit-page__history-col--review-note,
      .appointment-edit-page__history-col--reviewed {
        display: none;
      }

      .appointment-edit-page__id-meta {
        display: none;
      }
    }

    @media (max-width: 640px) {
      .appointment-edit-page__summary-grid {
        grid-template-columns: 1fr;
      }

      .appointment-edit-page__lock-toggle {
        align-items: flex-start;
        line-height: 1.4;
      }

      .appointment-edit-page__request-note textarea {
        font-size: 16px;
      }

      .appointment-edit-page__history {
        padding: 10px;
      }

      .appointment-edit-page__history-table th,
      .appointment-edit-page__history-table td {
        padding: 8px 10px;
        white-space: normal;
      }

      .appointment-edit-page__history-col--created,
      .appointment-edit-page__history-col--request-note {
        display: none;
      }

      .appointment-edit-page__diff {
        min-width: 160px;
      }

      .appointment-edit-page__history-table {
        min-width: 540px;
      }

      .appointment-edit-page__link-button {
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
      }
    }
  `]
})
export class AppointmentEditPageComponent implements OnInit, OnDestroy
{
  @Input()
  public appointmentIdOverride: string | null = null;

  @Input()
  public modalPresentation = false;

  @Input()
  public navigateOnSuccess = true;

  @Output()
  public readonly saved = new EventEmitter<void>();

  private readonly appointmentsService = inject(AppointmentsService);
  private readonly slotsService = inject(SlotsService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected isLoading = true;
  protected isSubmitting = false;
  protected loadErrorText: string | null = null;
  protected errorText: string | null = null;
  protected procedureTypes: ProcedureTypeItem[] = [];
  protected availableSlots: SlotItem[] = [];
  protected clinicOptions: Array<{ id: string; label: string; timeZone?: string }> = [];
  protected deviceOptions: Array<{
    id: string;
    label: string;
    deviceTypeId?: string;
    clinicId?: string;
  }> = [];
  protected specialistOptions: Array<{ id: string; label: string; procedureTypeIds?: string[] }> = [];
  protected clientOptions: Array<{ id: string; label: string; title: string }> = [];
  protected clinicProcedureAssignments: Record<string, string[]> = {};
  private clientTitleByClientId: Record<string, string> = {};
  protected lockedClientId: string | null = null;
  protected prefill: AppointmentFormPrefill = {
    clinicId: '',
    procedureTypeId: '',
    deviceId: '',
    firstSlotId: '',
    specialistUserId: ''
  };
  protected clientChangesLocked = false;
  protected isClientActor = false;
  protected requestNote = '';
  protected changeRequests: AppointmentChangeRequestItem[] = [];
  protected canOpenClient = false;
  protected canOpenDevice = false;
  protected canOpenClinic = false;
  protected canOpenProcedure = false;
  protected canOpenSpecialist = false;
  protected currentClinicLabel = '—';
  protected currentClinicId: string | null = null;
  protected currentProcedureLabel = '—';
  protected currentProcedureId: string | null = null;
  protected currentDeviceLabel = '—';
  protected currentDeviceId: string | null = null;
  protected currentSpecialistLabel = '—';
  protected currentSpecialistId: string | null = null;
  protected currentSlotSummary = '—';
  protected currentClinicTimeZone: string | null = null;
  protected clientMinCalendarDate: string | null = null;
  protected initialClientSpecialistIntent: AppointmentClientSpecialistIntent | null = null;
  protected isClientProfileModalOpen = false;
  protected isDeviceQuickViewOpen = false;
  protected isClinicQuickViewOpen = false;
  protected isProcedureQuickViewOpen = false;
  protected isSpecialistQuickViewOpen = false;
  protected selectedClientProfileId: string | null = null;
  protected selectedDeviceId: string | null = null;
  protected selectedClinicId: string | null = null;
  protected selectedProcedureTypeId: string | null = null;
  protected selectedSpecialistUserId: string | null = null;
  private appointmentId = '';
  private slotScopeKey = '';
  private slotScopeSubscription: Subscription | null = null;
  private currentAppointmentSlotIds: string[] = [];

  public ngOnInit(): void
  {
    const appointmentId = this.appointmentIdOverride?.trim() || this.route.snapshot.paramMap.get('id');

    if (!appointmentId)
    {
      this.loadErrorText = 'Failed to load appointments';
      this.isLoading = false;
      return;
    }

    const role = this.authService.getCurrentRole();
    this.appointmentId = appointmentId;
    this.isClientActor = role === 'client';
    this.clientMinCalendarDate = this.isClientActor ? this.todayDateKey() : null;
    this.canOpenClient =
      role === 'system_admin' ||
      role === 'company_admin' ||
      role === 'manager';
    this.canOpenDevice =
      role === 'system_admin' ||
      role === 'company_admin' ||
      role === 'manager' ||
      role === 'reception' ||
      role === 'client';
    this.canOpenClinic = this.canOpenDevice;
    this.canOpenProcedure = true;
    this.canOpenSpecialist = true;
    this.loadData();
  }

  public ngOnDestroy(): void
  {
    this.slotScopeSubscription?.unsubscribe();
  }

  protected onSubmitted(payload: CreateAppointmentRequest): void
  {
    if (!this.appointmentId)
    {
      return;
    }

    const selectedProcedure = this.procedureTypes.find(
      (item) => item.id === payload.procedureTypeId,
    );
    const procedureRequiresDevice = !!selectedProcedure?.deviceTypeId;
    const resolvedDeviceId = procedureRequiresDevice
      ? payload.deviceId?.trim() || this.prefill.deviceId?.trim() || ''
      : '';

    if (procedureRequiresDevice && !resolvedDeviceId)
    {
      this.errorText = 'Device ID is required for appointment update.';
      return;
    }

    const request: UpdateAppointmentRequest = {
      clinicId: payload.clinicId,
      procedureTypeId: payload.procedureTypeId,
      slotIds: payload.slotIds,
      deviceId: resolvedDeviceId || undefined,
      specialistUserId: payload.specialistUserId ?? null,
      confirmationRequired: payload.confirmationRequired,
      clientChangesLocked: this.isClientActor ? undefined : this.clientChangesLocked
    };

    this.isSubmitting = true;
    this.errorText = null;

    if (this.isClientActor)
    {
      this.appointmentsService.createChangeRequest(this.appointmentId, {
        clinicId: request.clinicId,
        procedureTypeId: request.procedureTypeId,
        slotIds: request.slotIds,
        deviceId: request.deviceId,
        specialistUserId: request.specialistUserId ?? null,
        note: this.requestNote.trim() || undefined,
      } as CreateAppointmentChangeRequest).subscribe({
        next: () =>
        {
          this.handleSaveSuccess();
        },
        error: (error: unknown) =>
        {
          this.isSubmitting = false;
          this.errorText = resolveHttpErrorMessageKey(error, {
            forbidden: 'error.forbiddenAppointmentAction',
            conflict: 'error.appointmentChangeRequestDuplicatePending',
            conflictByErrorCode: {
              appointment_change_request_duplicate_pending:
                'error.appointmentChangeRequestDuplicatePending'
            },
            fallback: 'error.appointmentActionFailed'
          });
        }
      });

      return;
    }

    this.appointmentsService.updateRequest(this.appointmentId, request).subscribe({
      next: () =>
      {
        this.handleSaveSuccess();
      },
      error: (error: unknown) =>
      {
        this.isSubmitting = false;
        this.errorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentAction',
          conflictByErrorCode: {
            appointment_client_time_overlap: 'error.appointmentClientTimeOverlap'
          },
          fallback: 'error.appointmentActionFailed'
        });
      }
    });
  }

  protected onClientLockChanged(event: Event): void
  {
    const target = event.target as HTMLInputElement | null;
    this.clientChangesLocked = !!target?.checked;
  }

  protected onRequestNoteInput(event: Event): void
  {
    const target = event.target as HTMLTextAreaElement | null;
    this.requestNote = target?.value ?? '';
  }

  protected snapshotValue(
    value: string | null | undefined,
    kind: 'clinic' | 'procedure' | 'device' | 'specialist',
  ): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (kind === 'clinic') {
      return this.clinicOptions.find((item) => item.id === normalized)?.label ?? this.readableId(normalized);
    }

    if (kind === 'procedure') {
      return this.procedureTypes.find((item) => item.id === normalized)?.name ?? this.readableId(normalized);
    }

    if (kind === 'device') {
      return this.deviceOptions.find((item) => item.id === normalized)?.label ?? this.readableId(normalized);
    }

    return this.specialistOptions.find((item) => item.id === normalized)?.label ?? this.readableId(normalized);
  }

  protected readableId(value: string | null | undefined): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (this.isUuidLike(normalized)) {
      return `${normalized.slice(0, 8)}...${normalized.slice(-6)}`;
    }

    if (normalized.length > 26) {
      return `${normalized.slice(0, 16)}...${normalized.slice(-6)}`;
    }

    return normalized;
  }

  protected showIdMeta(value: string | null | undefined): boolean
  {
    if (this.isClientActor) {
      return false;
    }

    const normalized = value?.trim() ?? '';
    return this.isUuidLike(normalized) || normalized.length > 26;
  }

  private loadData(): void
  {
    this.isLoading = true;
    this.loadErrorText = null;

    const appointment$ = this.appointmentsService.getDetailById(this.appointmentId).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    forkJoin({
      appointment: appointment$,
      appointmentSlots: appointment$.pipe(
        switchMap((appointment) =>
          appointment.slotIds.length > 0
            ? this.slotsService.getByIds(appointment.slotIds).pipe(
                catchError(() => of([] as SlotItem[])),
              )
            : of([] as SlotItem[])
        )
      ),
      changeRequests: this.appointmentsService.getChangeRequests(this.appointmentId).pipe(
        catchError(() => of([] as AppointmentChangeRequestItem[]))
      ),
      appointmentContext: this.appointmentsService.getClientCreateContext(),
    }).subscribe({
      next: ({
        appointment,
        appointmentSlots,
        changeRequests,
        appointmentContext,
      }: {
        appointment: AppointmentListItem;
        appointmentSlots: SlotItem[];
        changeRequests: AppointmentChangeRequestItem[];
        appointmentContext: ClientAppointmentCreateContextDto;
      }) =>
      {
        const procedureTypes: ProcedureTypeItem[] = appointmentContext.procedureTypes.map((item) => ({
          id: item.id,
          name: item.name,
          deviceTypeId: item.deviceTypeId,
          defaultDurationMinutes: item.defaultDurationMinutes,
          isActive: item.isActive,
        }));
        this.procedureTypes = procedureTypes;
        this.changeRequests = changeRequests;
        const sortedAppointmentSlots = [...appointmentSlots].sort((left, right) =>
          left.startTime.localeCompare(right.startTime),
        );
        const specialistSlot = sortedAppointmentSlots.find((slot) => slot.specialistUserId?.trim());
        const deviceSlot = sortedAppointmentSlots.find((slot) => slot.deviceId?.trim());
        const resolvedSpecialistUserId =
          appointment.specialistUserId?.trim() ||
          specialistSlot?.specialistUserId?.trim() ||
          '';
        const resolvedSpecialistLabel =
          appointment.specialistLabel?.trim() ||
          specialistSlot?.specialistLabel?.trim() ||
          '';
        const resolvedDeviceId =
          appointment.deviceId?.trim() ||
          deviceSlot?.deviceId?.trim() ||
          '';
        const initialSpecialistSlot =
          sortedAppointmentSlots.find((slot) =>
            !!resolvedSpecialistUserId &&
            slot.specialistUserId?.trim() === resolvedSpecialistUserId &&
            !slot.deviceId?.trim(),
          ) ||
          specialistSlot ||
          sortedAppointmentSlots[0] ||
          null;
        this.initialClientSpecialistIntent =
          this.isClientActor && resolvedSpecialistUserId ? 'specific' : null;
        if (this.isClientActor) {
          const clientClinic = appointmentContext.clinics.find((item) => item.id === appointment.clinicId);
          this.clinicOptions = [{
            id: appointment.clinicId,
            label: appointment.clinicLabel?.trim() || this.readableId(appointment.clinicId),
            timeZone: clientClinic?.timeZone,
          }];
          this.clinicProcedureAssignments = {
            [appointment.clinicId]: [appointment.procedureTypeId],
          };
          this.currentClinicTimeZone =
            appointment.clinicTimeZone?.trim() || clientClinic?.timeZone?.trim() || null;
          this.clientMinCalendarDate = this.todayDateKey(this.currentClinicTimeZone ?? undefined);

          this.deviceOptions = appointmentContext.devices.map((item) => ({
            id: item.id,
            deviceTypeId: item.deviceTypeId ?? undefined,
            clinicId: item.clinicId ?? undefined,
            label: item.label,
          }));
          if (
            resolvedDeviceId &&
            !this.deviceOptions.some((item) => item.id === resolvedDeviceId)
          ) {
            const currentProcedure = procedureTypes.find(
              (item) => item.id === appointment.procedureTypeId,
            );
            this.deviceOptions = [
              {
                id: resolvedDeviceId,
                deviceTypeId: currentProcedure?.deviceTypeId ?? undefined,
                clinicId: appointment.clinicId || deviceSlot?.clinicId,
                label: appointment.deviceLabel?.trim()
                  || deviceSlot?.deviceLabel?.trim()
                  || this.readableId(resolvedDeviceId),
              },
              ...this.deviceOptions,
            ];
          }

          this.specialistOptions = appointmentContext.specialists.map((item) =>
            item.id === resolvedSpecialistUserId
              ? {
                  ...item,
                  procedureTypeIds: Array.from(new Set([
                    ...(item.procedureTypeIds ?? []),
                    appointment.procedureTypeId,
                  ])),
                }
              : item
          );
          if (
            resolvedSpecialistUserId &&
            !this.specialistOptions.some((item) => item.id === resolvedSpecialistUserId)
          ) {
            this.specialistOptions = [
              {
                id: resolvedSpecialistUserId,
                label: resolvedSpecialistLabel || 'Assigned specialist',
                procedureTypeIds: [appointment.procedureTypeId],
              },
              ...this.specialistOptions,
            ];
          }

          this.clientOptions = [{
            id: appointment.clientId,
            label: 'You',
            title: 'You',
          }];
          this.clientTitleByClientId = {
            [appointment.clientId]: 'You',
          };
        } else {
          this.clinicOptions = appointmentContext.clinics.map((item) => ({
            id: item.id,
            label: item.label,
            timeZone: item.timeZone,
          }));
          this.currentClinicTimeZone =
            appointmentContext.clinics.find((item) => item.id === appointment.clinicId)?.timeZone?.trim()
            || appointment.clinicTimeZone?.trim()
            || null;
          this.deviceOptions = appointmentContext.devices.map((item) => ({
            id: item.id,
            deviceTypeId: item.deviceTypeId ?? undefined,
            clinicId: item.clinicId ?? undefined,
            label: item.label,
          }));
          this.specialistOptions = appointmentContext.specialists;
          const clientLabel = appointment.clientLabel?.trim() || this.readableId(appointment.clientId);
          this.clientOptions = [{
            id: appointment.clientId,
            label: clientLabel,
            title: clientLabel,
          }];
          this.clientTitleByClientId = {
            [appointment.clientId]: clientLabel,
          };
        }
        this.clinicProcedureAssignments = appointmentContext.clinicProcedureAssignments;
        this.availableSlots = [];
        this.lockedClientId = appointment.clientId;
        this.currentClinicLabel = appointment.clinicLabel?.trim()
          || this.clinicOptions.find((item) => item.id === appointment.clinicId)?.label
          || this.readableId(appointment.clinicId);
        this.currentClinicId = appointment.clinicId?.trim() || null;
        this.currentProcedureLabel =
          appointment.procedureTypeLabel?.trim()
          || procedureTypes.find((item) => item.id === appointment.procedureTypeId)?.name
          || this.readableId(appointment.procedureTypeId);
        this.currentProcedureId = appointment.procedureTypeId?.trim() || null;
        this.currentDeviceLabel = appointment.deviceLabel?.trim()
          || deviceSlot?.deviceLabel?.trim()
          || this.deviceOptions.find((item) => item.id === resolvedDeviceId)?.label
          || (resolvedDeviceId ? this.readableId(resolvedDeviceId) : '—');
        this.currentDeviceId = resolvedDeviceId || null;
        this.currentSpecialistLabel = resolvedSpecialistLabel
          || this.specialistOptions.find((item) => item.id === resolvedSpecialistUserId)?.label
          || (resolvedSpecialistUserId ? this.readableId(resolvedSpecialistUserId) : '—');
        this.currentSpecialistId = resolvedSpecialistUserId || null;
        this.currentAppointmentSlotIds = appointment.slotIds ?? [];
        this.currentSlotSummary = this.slotSnapshotSummary(appointment.slotIds ?? []);
        this.prefill = {
          clinicId: appointment.clinicId,
          procedureTypeId: appointment.procedureTypeId,
          deviceId: resolvedDeviceId,
          firstSlotId: '',
          firstSlotStartTime:
            initialSpecialistSlot?.startTime ??
            appointment.appointmentStartAt ??
            appointment.primarySlotStartAt ??
            undefined,
          specialistUserId: resolvedSpecialistUserId
        };
        this.clientChangesLocked = !!appointment.clientChangesLocked;
        this.isLoading = false;
        this.loadSlotsForScope({
          clinicId: appointment.clinicId,
          schedulingDate: this.schedulingDateKey(
            initialSpecialistSlot?.startTime ?? appointment.appointmentStartAt ?? appointment.primarySlotStartAt ?? '',
            appointment.clinicTimeZone ?? this.currentClinicTimeZone,
          ),
          procedureTypeId: appointment.procedureTypeId,
          specialistUserId: resolvedSpecialistUserId,
          deviceId: resolvedDeviceId,
        });
      },
      error: (error: unknown) =>
      {
        if (error instanceof Error && error.message === 'forbidden_or_not_found')
        {
          this.loadErrorText = 'error.forbiddenAppointmentsList';
          this.isLoading = false;
          return;
        }

        this.loadErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentsList',
          fallback: 'Failed to load appointments'
        });
        this.isLoading = false;
      }
    });
  }

  protected onSlotScopeChanged(scope: AppointmentSlotScopeChange): void
  {
    this.loadSlotsForScope(scope);
  }

  private loadSlotsForScope(scope: AppointmentSlotScopeChange): void
  {
    const normalizedScope = {
      clinicId: scope.clinicId?.trim() ?? '',
      schedulingDate: scope.schedulingDate.trim(),
      procedureTypeId: scope.procedureTypeId.trim(),
      specialistUserId: scope.specialistUserId?.trim() ?? '',
      deviceId: scope.deviceId?.trim() ?? '',
    };

    const requiresClinicScope = !this.isClientActor;
    if (
      (requiresClinicScope && !normalizedScope.clinicId) ||
      !normalizedScope.schedulingDate ||
      !normalizedScope.procedureTypeId
    ) {
      return;
    }

    const nextKey = [
      normalizedScope.clinicId,
      normalizedScope.schedulingDate,
      normalizedScope.procedureTypeId,
      this.isClientActor ? 'client-intent' : 'edit',
      normalizedScope.specialistUserId,
      normalizedScope.deviceId,
    ].join('|');

    if (nextKey === this.slotScopeKey) {
      return;
    }

    this.slotScopeKey = nextKey;
    this.slotScopeSubscription?.unsubscribe();
    this.slotScopeSubscription = this.loadScopedSlots(normalizedScope).subscribe({
      next: (slots) => {
        if (this.slotScopeKey !== nextKey) {
          return;
        }

        this.availableSlots = slots;
        this.currentSlotSummary = this.slotSnapshotSummary(this.currentAppointmentSlotIds);
      },
      error: (error: unknown) => {
        if (this.slotScopeKey !== nextKey) {
          return;
        }

        console.error('Failed to load appointment edit slots', error);
        this.availableSlots = [];
      },
    });
  }

  private loadScopedSlots(scope: AppointmentSlotScopeChange): Observable<SlotItem[]>
  {
    const statuses: SlotStatus[] = ['available', 'reserved'];

    if (this.isClientActor) {
      const specialistUserId = scope.specialistUserId?.trim() ?? '';

      const requests = statuses.flatMap((status) => {
        if (!specialistUserId) {
          return [
            this.slotsService.getList({
              clinicId: scope.clinicId || undefined,
              procedureTypeId: scope.procedureTypeId,
              schedulingDate: scope.schedulingDate,
              status,
            }),
          ];
        }

        const selectedProcedure = this.procedureTypes.find(
          (item) => item.id === scope.procedureTypeId,
        );
        const compatibleDeviceIds = selectedProcedure?.deviceTypeId
          ? this.deviceOptions
              .filter((item) =>
                item.deviceTypeId === selectedProcedure.deviceTypeId &&
                (!scope.clinicId || !item.clinicId || item.clinicId === scope.clinicId),
              )
              .map((item) => item.id)
          : [];

        return [
          this.slotsService.getList({
            clinicId: scope.clinicId || undefined,
            procedureTypeId: scope.procedureTypeId,
            schedulingDate: scope.schedulingDate,
            specialistUserId,
            status,
          }),
          ...compatibleDeviceIds.map((deviceId) =>
            this.slotsService.getList({
              clinicId: scope.clinicId || undefined,
              procedureTypeId: scope.procedureTypeId,
              schedulingDate: scope.schedulingDate,
              deviceId,
              status,
            })
          ),
        ];
      });

      return forkJoin(requests).pipe(
        map((slotGroups) => {
          const byId = new Map<string, SlotItem>();
          for (const slots of slotGroups) {
            for (const slot of slots) {
              byId.set(slot.id, slot);
            }
          }

          return Array.from(byId.values()).sort((left, right) =>
            left.startTime.localeCompare(right.startTime),
          );
        }),
      );
    }

    const requests = statuses.flatMap((status) => {
      const specialistRequest = this.slotsService.getPage({
        limit: 200,
        sort: 'startAtAsc',
        filters: {
          clinicId: scope.clinicId,
          specialistUserId: scope.specialistUserId,
          procedureTypeId: scope.procedureTypeId,
          schedulingDate: scope.schedulingDate,
          status,
        },
      });

      const deviceRequest = scope.deviceId
        ? this.slotsService.getPage({
            limit: 200,
            sort: 'startAtAsc',
            filters: {
              clinicId: scope.clinicId,
              deviceId: scope.deviceId,
              procedureTypeId: scope.procedureTypeId,
              schedulingDate: scope.schedulingDate,
              status,
            },
          })
        : null;

      return deviceRequest ? [specialistRequest, deviceRequest] : [specialistRequest];
    });

    return forkJoin(requests).pipe(
      map((pages) => {
        const byId = new Map<string, SlotItem>();
        for (const page of pages) {
          for (const slot of page.items) {
            byId.set(slot.id, slot);
          }
        }

        return Array.from(byId.values()).sort((left, right) =>
          left.startTime.localeCompare(right.startTime),
        );
      }),
    );
  }

  private schedulingDateKey(value: string, timeZone: string | null | undefined): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '';
    }

    const parsed = new Date(normalized);
    if (!Number.isNaN(parsed.getTime())) {
      return new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: timeZone?.trim() || undefined,
      }).format(parsed);
    }

    return normalized.slice(0, 10);
  }

  private todayDateKey(timeZone?: string): string
  {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timeZone?.trim() || undefined,
    }).format(new Date());
  }

  private withTenantPath(segments: Array<string>): Array<string>
  {
    const role = this.authService.getCurrentRole();
    const slug = this.authService.getPreferredCompanySlug();

    if (role !== 'system_admin' && slug)
    {
      return ['/', slug, ...segments];
    }

    return ['/', ...segments];
  }

  protected openClient(clientId: string): void
  {
    if (!this.canOpenClient || !clientId.trim()) {
      return;
    }

    this.selectedClientProfileId = clientId.trim();
    this.isClientProfileModalOpen = true;
  }

  protected closeClientProfileModal(): void {
    this.isClientProfileModalOpen = false;
    this.selectedClientProfileId = null;
  }

  protected openDevice(deviceId: string): void
  {
    const normalized = deviceId.trim();
    if (!this.canOpenDevice || !normalized) {
      return;
    }

    this.selectedDeviceId = normalized;
    this.isDeviceQuickViewOpen = true;
  }

  protected closeDeviceQuickView(): void
  {
    this.isDeviceQuickViewOpen = false;
    this.selectedDeviceId = null;
  }

  protected openClinic(clinicId: string): void
  {
    const normalized = clinicId.trim();
    if (!this.canOpenClinic || !normalized) {
      return;
    }

    this.selectedClinicId = normalized;
    this.isClinicQuickViewOpen = true;
  }

  protected closeClinicQuickView(): void
  {
    this.isClinicQuickViewOpen = false;
    this.selectedClinicId = null;
  }

  protected openProcedure(procedureTypeId: string): void
  {
    const normalized = procedureTypeId.trim();
    if (!this.canOpenProcedure || !normalized) {
      return;
    }

    this.selectedProcedureTypeId = normalized;
    this.isProcedureQuickViewOpen = true;
  }

  protected closeProcedureQuickView(): void
  {
    this.isProcedureQuickViewOpen = false;
    this.selectedProcedureTypeId = null;
  }

  protected openSpecialist(specialistUserId: string): void
  {
    const normalized = specialistUserId.trim();
    if (!this.canOpenSpecialist || !normalized) {
      return;
    }

    this.selectedSpecialistUserId = normalized;
    this.isSpecialistQuickViewOpen = true;
  }

  protected closeSpecialistQuickView(): void
  {
    this.isSpecialistQuickViewOpen = false;
    this.selectedSpecialistUserId = null;
  }

  protected clientLabel(clientId: string | null | undefined): string
  {
    const normalized = clientId?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    return this.clientOptions.find((item) => item.id === normalized)?.label ?? this.readableId(normalized);
  }

  protected clientTitle(clientId: string | null | undefined): string
  {
    const normalized = clientId?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    return this.clientTitleByClientId[normalized] ?? this.clientLabel(normalized);
  }

  private isUuidLike(value: string): boolean
  {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }

  protected slotSnapshotSummary(slotIds: string[] | null | undefined): string
  {
    const normalizedIds = (slotIds ?? []).filter((slotId) => slotId?.trim().length);
    if (normalizedIds.length === 0) {
      return '—';
    }

    const matchedSlots = normalizedIds
      .map((slotId) => this.availableSlots.find((slot) => slot.id === slotId))
      .filter((slot): slot is SlotItem => !!slot);

    if (matchedSlots.length === 0) {
      return `${normalizedIds.length} slots`;
    }

    const sorted = [...matchedSlots].sort((left, right) =>
      new Date(left.startTime).getTime() - new Date(right.startTime).getTime(),
    );
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    return this.formatSlotRange(first.startTime, last.endTime, normalizedIds.length);
  }

  private formatSlotRange(startTime: string, endTime: string, slotCount?: number): string
  {
    const start = new Date(startTime);
    const end = new Date(endTime);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return slotCount ? `${slotCount} slots` : `${startTime} -> ${endTime}`;
    }

    const dateText = new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: this.currentClinicTimeZone ?? undefined,
    }).format(start);
    const timeText = new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: this.currentClinicTimeZone ?? undefined,
    });
    const countSuffix = slotCount ? ` (${slotCount} slots)` : '';

    return `${dateText} ${timeText.format(start)} -> ${timeText.format(end)}${countSuffix}`;
  }

  private handleSaveSuccess(): void
  {
    if (this.navigateOnSuccess) {
      void this.router.navigate(this.withTenantPath(['appointments']));
      return;
    }

    this.saved.emit();
  }
}
